import toString from 'lodash/toString'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { produce } from 'immer'
import isError from 'lodash/isError'
import { Check, Plus } from 'lucide-react'
import { Card } from '@klear/ui/components/card'
import { toast } from '@klear/ui/hooks/use-toast'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import { ROUTE_CUSTOMERS, buildNavPathTrader } from '@klear/constants'
import {
  clearOrderFormErrors,
  hasOrderFormErrors,
  EMPTY_ORDER_FORM,
  orderFormToCreateWire,
  validateOrderForm,
  type OrderForm,
  type OrderFormErrorMap
} from '@klear/order/wire.client'
import { OrderFields } from '@/app/order/workflow/order-fields'
import { useOrderMutations } from '@/app/order/workflow/use-order'
import { CreateEntityReturnLink } from '@/app/workflow/create-entity-return-link'

export type OrderStepGroupResult = {
  orderId: number
}

const STEP = {
  DETAILS: 'order-details'
} as const

type OrderStepCtx = {
  traderId: number
  vals: OrderForm
  errors: OrderFormErrorMap
  handleFieldChange: <K extends keyof OrderForm>(field: K, value: OrderForm[K]) => void
}

const detailsStep: WizardStepModule<OrderStepCtx> = {
  id: STEP.DETAILS,
  label: 'Details',
  isComplete: (ctx) => !hasOrderFormErrors(validateOrderForm(ctx.vals)),
  component: function OrderDetailsStepContent({ ctx }) {
    return (
      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Create a new order</h2>
        <p className="text-sm text-muted-foreground">
          Start a new order for a customer, then build its purchase order in the next step.
        </p>
        <OrderFields
          traderId={ctx.traderId}
          values={ctx.vals}
          errors={ctx.errors}
          onFieldChange={ctx.handleFieldChange}
          createCustomerAction={
            <CreateEntityReturnLink to={buildNavPathTrader(toString(ctx.traderId), ROUTE_CUSTOMERS, 'new')}>
              <Plus className="h-3 w-3 mr-1" />
              Create customer
            </CreateEntityReturnLink>
          }
        />
      </Card>
    )
  }
}

const ORDER_STEP_MODULES = [detailsStep]

export type OrderFlowOptions = {
  traderId: number
}

export type OrderFlowProps = OrderFlowOptions & {
  cancelOnFirstStep?: boolean
  onNavStateChange: (nav: WizardStepGroupNavState) => void
  onComplete: (result: OrderStepGroupResult) => void
  onCancel: () => void
  onDirtyChange?: (dirty: boolean) => void
}

export function OrderFlow({
  traderId,
  cancelOnFirstStep = false,
  onNavStateChange,
  onComplete,
  onCancel,
  onDirtyChange
}: OrderFlowProps) {
  const { createOrder, isCreating } = useOrderMutations()
  const [values, setValues] = useState<OrderForm>(() => ({ ...EMPTY_ORDER_FORM }))
  const [errors, setErrors] = useState<OrderFormErrorMap>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  const handleFieldChange = useCallback(<K extends keyof OrderForm>(field: K, value: OrderForm[K]) => {
    setDirty(true)
    setValues((current) =>
      produce(current, (draft) => {
        draft[field] = value as never
      })
    )
    setErrors((current) => clearOrderFormErrors(current, [field]))
  }, [])

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateOrderForm(values)
    if (hasOrderFormErrors(validationErrors)) {
      setErrors(validationErrors)
      return
    }
    setSaveError(null)

    try {
      const result = await createOrder({ traderId, data: orderFormToCreateWire(values) })
      if (result?.id == null) {
        throw new Error('Creating the order did not return an id.')
      }
      setDirty(false)
      onComplete({ orderId: result.id })
    } catch (err: unknown) {
      const message = isError(err) ? err.message : 'Failed to create the order.'
      setSaveError(message)
      toast({ title: 'Create failed', description: message, variant: 'destructive' })
    }
  }, [createOrder, onComplete, traderId, values])

  const stepCtx = useMemo<OrderStepCtx>(
    () => ({
      traderId,
      vals: values,
      errors,
      handleFieldChange
    }),
    [traderId, values, errors, handleFieldChange]
  )

  const stepDefs = useMemo(() => bindWizardSteps(ORDER_STEP_MODULES, stepCtx), [stepCtx])

  const flow = useReportedWizardFlow(stepDefs, {
    stepSelection: 'gated',
    onNavStateChange,
    onExitFirstStep: cancelOnFirstStep ? onCancel : undefined
  })

  return (
    <WizardFlowContent
      flow={flow}
      onSubmit={() => void handleSubmit()}
      cancelOnFirstStep={cancelOnFirstStep}
      disabled={isCreating}
      submitDisabled={isCreating || !detailsStep.isComplete?.(stepCtx)}
      submitIcon={<Check className="h-4 w-4 mr-1" />}
      submitLabel={isCreating ? 'Creating…' : 'Create order'}
      navClassName="flex justify-between gap-2"
    >
      {saveError ? <p className="text-sm text-destructive">{saveError}</p> : null}
    </WizardFlowContent>
  )
}
